import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell
} from 'recharts';
import recruitingService from '../utils/recruitingService';
import './HiresByJobTitleChart.css';

/**
 * HiresByJobTitleChart Component
 * Horizontal bar chart showing hires by job title
 */
function HiresByJobTitleChart({ filters }) {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const response = await recruitingService.getHiresByJobTitle(filters);
            if (response.success && response.data) {
                // Truncate long job titles
                const formattedData = response.data.map(d => ({
                    ...d,
                    displayTitle: d.jobTitle.length > 25 ? d.jobTitle.substring(0, 25) + '...' : d.jobTitle
                }));
                setData(formattedData);
            }
        } catch (err) {
            console.error('Error fetching hires by job title:', err);
            setData([]);
        }
        setLoading(false);
    }, [filters]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Color gradient for bars
    const getBarColor = (index) => {
        const colors = ['#27AE60', '#2ECC71', '#58D68D', '#82E0AA', '#ABEBC6', '#D5F5E3', '#E8F8F5', '#F0FFF0', '#F5F5F5', '#F8F8F8'];
        return colors[index] || colors[colors.length - 1];
    };

    // Custom tooltip
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="chart-tooltip">
                    <p className="tooltip-label">{data.jobTitle}</p>
                    <p className="tooltip-value">
                        <span className="tooltip-count">{data.hires}</span> hires
                    </p>
                </div>
            );
        }
        return null;
    };

    if (loading) {
        return (
            <div className="hires-by-job-title-chart chart-loading">
                <div className="chart-loading-spinner"></div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="hires-by-job-title-chart chart-empty">
                <p>No hires data by job title available</p>
            </div>
        );
    }

    // Calculate total for header
    const totalHires = data.reduce((sum, d) => sum + d.hires, 0);

    return (
        <div className="hires-by-job-title-chart">
            <div className="chart-header">
                <div className="chart-title-section">
                    <h3 className="chart-title">Hires by Job Title</h3>
                    <span className="chart-subtitle">Top 10 positions</span>
                </div>
                <div className="chart-stats">
                    <span className="stat-value">{totalHires}</span>
                    <span className="stat-label">Total</span>
                </div>
            </div>
            <div className="chart-body">
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                        data={data}
                        layout="vertical"
                        margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis
                            type="number"
                            tick={{ fontSize: 12, fill: '#7F8C8D' }}
                            tickLine={false}
                            axisLine={{ stroke: '#e5e7eb' }}
                        />
                        <YAxis
                            type="category"
                            dataKey="displayTitle"
                            tick={{ fontSize: 11, fill: '#2C3E50' }}
                            tickLine={false}
                            axisLine={false}
                            width={130}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(230, 126, 34, 0.1)' }} />
                        <Bar dataKey="hires" radius={[0, 4, 4, 0]} maxBarSize={25}>
                            {data.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getBarColor(index)} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default HiresByJobTitleChart;
